import { TokenInterface } from '@/core/tokens';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Address, LiquidityPool, Vault, Zero } from 'nftx.js';
import Price from '../../elements/Price';
import { useNetwork } from '@/presentation/context/network';
import { useMemo } from 'react';
import Icon, { Icons } from '../../elements/Icon';
import useCollection from '@/application/read/collections/useCollection';
import {
  getBestBuyPrice,
  getBestSellPrice,
} from '@/application/read/vaults/utils';
import { getCollectionPath } from '@/presentation/utils/paths';
import { useModalContext } from '@/presentation/context/modal';

const CollectionRow = ({
  collectionAddress,
  vaults,
  tokens,
  pools,
}: {
  collectionAddress: Address;
  vaults: Vault[];
  tokens: TokenInterface[];
  pools: LiquidityPool[];
}) => {
  const { push } = useRouter();
  const { slug } = useNetwork();
  const { showCreatePositionModal, showCreateLiquidityPositionModal } =
    useModalContext();

  const { data: meta } = useCollection(collectionAddress);

  const bestBuyPrice = useMemo(() => {
    return getBestBuyPrice(vaults);
  }, [vaults]);

  const bestSellPrice = useMemo(() => {
    return getBestSellPrice(vaults);
  }, [vaults]);

  const noItems = vaults.reduce((acc, cur) => acc + cur.totalHoldings, 0) === 0;
  const noLiquidity = bestBuyPrice === 0n && bestSellPrice === 0n;

  return (
    <tr
      onClick={() =>
        push(
          getCollectionPath({
            chain: slug,
            collectionSlug: meta?.slug ?? collectionAddress,
          })
        )
      }
      className="hover:cursor-pointer hover:bg-gray-100 leading-none"
    >
      <td className="px-3 py-4">
        <Link
          href={getCollectionPath({
            chain: slug,
            collectionSlug: meta?.slug ?? collectionAddress,
          })}
          className="inline-flex items-center min-h-[2rem]"
        >
          {meta?.image && (
            <img
              src={meta.image}
              alt={meta.name}
              className="flex-none w-8 h-8 mr-2 rounded-md overflow-hidden object-cover"
              loading="lazy"
            />
          )}
          <span className="leading-none">{vaults[0].asset.name}</span>
        </Link>
      </td>
      <td className="p-3 text-right">
        {vaults.reduce((acc, cur) => cur.totalHoldings + acc, 0)}
      </td>
      {noItems ? (
        <td colSpan={3} className="p-3 text-center">
          <div className="flex items-center space-x-4 px-6">
            <span className="flex-1 h-px bg-gray-300" />
            <span className="font-medium text-sm inline-flex items-center px-4">
              <Icon
                name={Icons.GHOST}
                size={24}
                className="text-gray-500 mr-2"
              />
              <span className="mr-6 text-gray-500">{'No items'}</span>
              <button
                type="button"
                className="text-pink-500 underline hover:no-underline p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  showCreatePositionModal({
                    isNewVault: false,
                    vaultId: vaults[0].vaultId,
                  });
                }}
              >
                {'Add to pool'}
              </button>
            </span>
            <span className="flex-1 h-px bg-gray-300" />
          </div>
        </td>
      ) : noLiquidity ? (
        <td colSpan={3} className="p-3 text-center">
          <div className="flex items-center space-x-4 px-6">
            <span className="flex-1 h-px bg-gray-300" />
            <span className="font-medium text-sm inline-flex items-center px-4">
              <Icon
                name={Icons.NO_SYMBOL}
                size={24}
                className="text-gray-500 mr-2"
              />
              <span className="mr-6 text-gray-500">{'No liquidity'}</span>
              <button
                type="button"
                className="text-pink-500 underline hover:no-underline p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  showCreatePositionModal({
                    isNewVault: false,
                    vaultId: vaults[0].vaultId,
                    poolType: 'pair',
                  });
                }}
              >
                {'Add to pool'}
              </button>
            </span>
            <span className="flex-1 h-px bg-gray-300" />
          </div>
        </td>
      ) : (
        <>
          <td className="p-3 text-right">
            {bestBuyPrice === 0n ? (
              <span className="font-medium text-sm inline-flex items-center justify-end">
                <Icon
                  name={Icons.DROP_HALF_BOTTOM}
                  size={20}
                  className="text-gray-500 mr-2"
                />
                <button
                  type="button"
                  className="text-pink-500 underline hover:no-underline p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    showCreateLiquidityPositionModal({
                      isNewVault: false,
                      vaultId: vaults[0].vaultId,
                      poolType: 'nft',
                    });
                  }}
                >
                  {'Add NFTs to pool'}
                </button>
              </span>
            ) : (
              <>
                {vaults.length == 1 && (
                  <span className="text-xs mr-1 text-gray-500">{'From'}</span>
                )}
                <Price value={bestBuyPrice} />
              </>
            )}
          </td>
          <td className="p-3 text-right">
            {bestSellPrice === 0n ? (
              <span className="font-medium text-sm inline-flex items-center justify-end">
                <Icon
                  name={Icons.DROP_HALF_BOTTOM}
                  size={20}
                  className="text-gray-500 mr-2"
                />
                <button
                  type="button"
                  className="text-pink-500 underline hover:no-underline p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    showCreateLiquidityPositionModal({
                      isNewVault: false,
                      vaultId: vaults[0].vaultId,
                      poolType: 'eth',
                    });
                  }}
                >
                  {'Add ETH to pool'}
                </button>
              </span>
            ) : (
              <>
                {vaults.length == 1 && (
                  <span className="text-xs mr-1 text-gray-500">{'From'}</span>
                )}
                <Price value={bestSellPrice} />
              </>
            )}
          </td>
          <td className="p-3 text-right">
            <Price
              value={pools.reduce(
                (total, pool) => total + pool.weeklyVolume,
                0n
              )}
              showEmpty
            />
          </td>
        </>
      )}
    </tr>
  );
};

export default CollectionRow;
